import React, { Dispatch, SetStateAction, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Form } from 'react-bootstrap';
import { format, startOfMonth, startOfWeek, startOfYear, subDays } from 'date-fns';

import { UseOutletContextSale } from '../Sale';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { Quote } from '../../../../models/quote.model';
import { Order } from '../../../../models/order.model';
import { Invoice } from '../../../../models/invoice.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { QuoteGroup } from '../../../../models/quote.group.model';
import { OrderGroup } from '../../../../models/order.group.model';
import { InvoiceGroup } from '../../../../models/invoice.group.model';
import { modalShow } from '../../../../utils/modal.util';
import { statusInvoiceList, statusOrderList, statusQuoteList, statusDocumentValue } from '../../../../libraries/status.document.library';
import { validityList, validityValue } from '../../../../libraries/validation.library';
import { paymentStatusList, paymentStatusValue } from '../../../../libraries/payment.library';
import { expirationList, expirationValue } from '../../../../libraries/expiration.library';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { contactSelected } from '../../../../scripts/list.selected.script';
import { companyYearList } from '../../../../scripts/list.filter.script';
import { ApiDigitalAdminContact } from '../../../../services/admin/contact.service';
import { normalizeContacts } from '../../../../scripts/format.value.script';

import AppModalQuoteExport from '../../../../components/modal/ModalQuoteExport';
import AppModalOrderExport from '../../../../components/modal/ModalOrderExport';
import AppModalInvoiceExport from '../../../../components/modal/ModalInvoiceExport';
import AppStorageRemove from '../../../../components/element/StorageRemove';
import { IconPlusCircle, IconArrowsSort, IconSearch, IconFilter, IconSalelistheader7, IconDownload, IconCross } from '../../../../assets/svg';

export interface AppSaleListHeaderProps {
  sale: string,
  storage: any,
  button: {filter: boolean, group: boolean},
  filter: {interval: string, start_date: string, end_date: string, contact: string, contact_label: string, validity: string, status: string, expiration: string, payment: string, sort_field: string, sort_mode: string, group: string, search: string},
  records: any[] | Quote[] | Order[] | Invoice[],
  recordGroups: any[] | QuoteGroup[] | OrderGroup[] | InvoiceGroup[],
  setFilter: Dispatch<SetStateAction<{interval: string, start_date: string, end_date: string, contact: string, contact_label: string, validity: string, status: string, expiration: string, payment: string, sort_field: string, sort_mode: string, group: string, search: string}>>,
  loadDocuments: Function,
  totalItems?: number
};

const AppSaleListHeader: React.FunctionComponent<AppSaleListHeaderProps> = ({sale, storage, button, filter, records, recordGroups, setFilter, loadDocuments, totalItems}) => {
  const {companyForUser, settingsForCompany, treasuryAccountsForCompany, contactsForCompany} = UseOutletContextSale()
  const vatSubject = (settingsForCompany?.subject_tax ?? companyForUser?.subject_tax) === true
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const handleChangeInterval = (event: React.ChangeEvent <HTMLFormElement | HTMLSelectElement>) => {
    let startDate: Date = new Date()
    let endDate: Date = new Date()

    switch (event.target.value) {
      case 'today':
        setFilter({...filter, interval: event.target.value, start_date: format(startDate, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`), end_date: format(endDate, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)})
        break;
      case 'yesterday':
        setFilter({...filter, interval: event.target.value, start_date: format(subDays(startDate, 1), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`), end_date: format(subDays(endDate, 1), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)})
        break;
      case 'week':
        setFilter({...filter, interval: event.target.value, start_date: format(startOfWeek(startDate), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`), end_date: format(endDate, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)})
        break;
      case 'month':
        setFilter({...filter, interval: event.target.value, start_date: format(startOfMonth(startDate), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`), end_date: format(endDate, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)})
        break;
      case 'year':
        setFilter({...filter, interval: event.target.value, start_date: format(startOfYear(startDate), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`), end_date: format(endDate, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)})
        break;
      default:
        setFilter({...filter, interval: event.target.value, start_date: format((new Date(`${event.target.value}-01-01T00:00:00.000`)), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`), end_date: format((new Date(`${event.target.value}-12-31T23:59:59.999`)), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)})
        break;
    }
  }

  const handleChangeStartDate = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let date: string = (event.target.value.length > 0) ? format((new Date(`${event.target.value}T00:00:00.000`)), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`) : ''
    setFilter({...filter, interval: '', start_date: date})
  }

  const handleChangeEndDate = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let date: string = (event.target.value.length > 0) ? format((new Date(`${event.target.value}T23:59:59.999`)), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`) : ''
    setFilter({...filter, interval: '', end_date: date})
  }

  const [contactSearch, setContactSearch] = useState<string>('')
  const [contactDropdownOpen, setContactDropdownOpen] = useState<boolean>(false)
  const [contactResults, setContactResults] = useState<any[]>([])
  const contactAutocompleteRef = useRef<HTMLDivElement>(null)
  const contactSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const contactSearchAbortRef = useRef<AbortController | null>(null)

  const getContactDisplayName = (item: any): string => {
    return item.type === "person" ? `${item.data.first_name ?? ''} ${item.data.last_name ?? ''}`.trim() : `${item.data.name ?? ''}`
  }

  const handleSelectContact = (contactId: string) => {
    const selected = contactResults.find((c: any) => c.id === contactId)
      ?? (contactsForCompany ?? []).find((c: any) => c.id === contactId)
    if (selected) {
      const label = getContactDisplayName(selected)
      setContactSearch(label)
      setFilter({...filter, contact: contactId, contact_label: label})
    }
    setContactDropdownOpen(false)
  }

  const handleClearContact = () => {
    setContactSearch('')
    setContactResults([])
    setFilter({...filter, contact: '', contact_label: ''})
    setContactDropdownOpen(false)
    if (contactSearchTimerRef.current) clearTimeout(contactSearchTimerRef.current)
    if (contactSearchAbortRef.current) contactSearchAbortRef.current.abort()
  }

  const handleContactInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!companyForUser) return
    const value = e.target.value
    setContactSearch(value)

    setFilter({...filter, contact: ''})

    if (value.length < 2) {
      setContactDropdownOpen(false)
      setContactResults([])
      if (contactSearchTimerRef.current) clearTimeout(contactSearchTimerRef.current)
      if (contactSearchAbortRef.current) contactSearchAbortRef.current.abort()
      return
    }

    setContactDropdownOpen(true)

    if (contactSearchTimerRef.current) clearTimeout(contactSearchTimerRef.current)
    if (contactSearchAbortRef.current) contactSearchAbortRef.current.abort()

    contactSearchTimerRef.current = setTimeout(() => {
      const abort = new AbortController()
      contactSearchAbortRef.current = abort
      ApiDigitalAdminContact.getContacts(companyForUser.id, value, '', '', '', 1, 20, abort.signal)
        .then((response: any) => {
          if (response.status === 0) return
          if (response.status === 200) {
            setContactResults(normalizeContacts(response.data.items))
          }
        })
    }, 300)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contactAutocompleteRef.current && !contactAutocompleteRef.current.contains(event.target as Node)) {
        setContactDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (filter.contact === '') {
      setContactSearch('')
    } else if (filter.contact_label) {
      setContactSearch(filter.contact_label)
    } else {
      const found = (contactsForCompany ?? []).find((c: any) => c.id === filter.contact)
      if (found) {
        setContactSearch(getContactDisplayName(found))
      } else if (companyForUser) {
        ApiDigitalAdminContact.getContact(companyForUser.id, filter.contact).then((response: any) => {
          if (response.status === 200 && response.data) {
            setContactSearch(getContactDisplayName(response.data))
          }
        })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.contact, contactsForCompany])

  const handleChangeValidity = (event: React.ChangeEvent <HTMLFormElement | HTMLSelectElement>) => {
    setFilter({...filter, validity: event.target.value})
  }

  const handleChangeStatus = (event: React.ChangeEvent <HTMLFormElement | HTMLSelectElement>) => {
    setFilter({...filter, status: event.target.value})
  }

  const handleChangeExpiration = (event: React.ChangeEvent <HTMLFormElement | HTMLSelectElement>) => {
    setFilter({...filter, expiration: event.target.value})
  }

  const handleChangePayment = (event: React.ChangeEvent <HTMLFormElement | HTMLSelectElement>) => {
    setFilter({...filter, payment: event.target.value})
  }

  const executeGroupList = (item: string) => {
    if (companyForUser) {
      setFilter({...filter, group: item, interval: '', start_date: '', end_date: '', contact: '', contact_label: '', validity: '', status: '', expiration: '', payment: '', sort_field: '', sort_mode: ''})
      loadDocuments(companyForUser.id)
    }
  }

  const executeFilterRemove = (item: string) => {
    if (companyForUser && (filter.interval.length !== 0 || filter.start_date.length !== 0 || filter.end_date.length !== 0 || filter.contact.length !== 0 || filter.validity.length !== 0 || filter.status.length !== 0 || filter.expiration.length !== 0 || filter.payment.length !== 0)) {
      const newFilter = {...filter, [item]: ''}
      if (item === "start_date" || item === "end_date") newFilter.interval = ''
      if (item === "interval") { newFilter.start_date = ''; newFilter.end_date = '' }
      if (item === "contact") newFilter.contact_label = ''
      setFilter(newFilter)
      loadDocuments(companyForUser.id)
    }
  }

  const executeFilterReset = () => {
    if (companyForUser && (filter.interval.length !== 0 || filter.start_date.length !== 0 || filter.end_date.length !== 0 || filter.contact.length !== 0 || filter.validity.length !== 0 || filter.status.length !== 0 || filter.expiration.length !== 0 || filter.payment.length !== 0)) {
      setFilter({...filter, interval: '', start_date: '', end_date: '', contact: '', contact_label: '', validity: '', status: '', expiration: '', payment: '', sort_field: '', sort_mode: ''})
      loadDocuments(companyForUser.id)
    }
  }

  const executeFilterApply = () => {
    if (companyForUser && (filter.interval.length !== 0 || filter.start_date.length !== 0 || filter.end_date.length !== 0 || filter.contact.length !== 0 || filter.validity.length !== 0 || filter.status.length !== 0 || filter.expiration.length !== 0 || filter.payment.length !== 0)) {
      setFilter({...filter, sort_field: '', sort_mode: '', group: ''})
      loadDocuments(companyForUser.id)
    }
  }

  const executeNavigateCreate = () => {
    if ((treasuryAccountsForCompany ?? []).length > 0) {
      navigate(`/main/sale/${sale}/create`)
    } else {
      GlobalSwal.fire({
        title: lang.labels.thereAreNoTreasuryAccounts,
        text: lang.labels.toCreateAnDocument,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesCreateTreasuryAccount,
        cancelButtonText: lang.labels.noCancel
      }).then( (result) => {
        if (result.isConfirmed) {
          navigate('/main/treasury/create')
        }
      })
    }
  }

  const [searchText, setSearchText] = useState(filter.search)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
      if (contactSearchTimerRef.current) clearTimeout(contactSearchTimerRef.current)
      if (contactSearchAbortRef.current) contactSearchAbortRef.current.abort()
    }
  }, [])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchText(value)

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)

    searchTimerRef.current = setTimeout(() => {
      setFilter({...filter, search: value})
      if (companyForUser) loadDocuments(companyForUser.id)
    }, 400)
  }

  const executeShowModalExport = () => {
    modalShow(`modal-${sale}-export`)
  }

  function status(): {code: string, display: string}[] {
    switch (sale) {
      case 'quote':
        return statusQuoteList(lang)
      case 'order':
        return statusOrderList(lang)
      case 'invoice':
        return statusInvoiceList(lang)
      default:
        return []
    }
  }

  return (
    <>
      { companyForUser && records && recordGroups &&
        <>
          <div className="card-header">
            <div className="card-title">
              <div className="badge badge-lg badge-light-primary">
                <div className="d-flex align-items-center flex-wrap">
                  <span className="svg-icon svg-icon-2 svg-icon-primary me-3">
                    <IconPlusCircle />
                  </span>
                  <span>{lang.labels[`${sale}s` as keyof typeof lang.labels]}</span>
                  <span className="svg-icon svg-icon-2 svg-icon-primary mx-1">
                    <IconArrowsSort />
                  </span>
                  <span className="text-capitalize">{button.group ? lang.labels.groups : lang.labels.listing}</span>
                  <span className="svg-icon svg-icon-2 svg-icon-primary mx-1">
                    <IconArrowsSort />
                  </span>
                  <span>{button.group ? `${recordGroups.length} ${lang.labels.collections}` : `${totalItems ?? records.length} ${lang.labels.items}`}</span>
                </div>
              </div>
            </div>
            <div className="card-toolbar">
              <div className="d-flex align-items-center position-relative mb-1 me-2">
                <span className="svg-icon svg-icon-1 position-absolute ms-4">
                  <IconSearch />
                </span>
                <input
                  type="text"
                  className="form-control w-250px ps-14"
                  placeholder={lang.labels.search}
                  value={searchText}
                  onChange={handleSearchChange}
                />
              </div>
              <Dropdown>
                <Dropdown.Toggle variant={`btn ${button.filter ? "btn-primary" : "btn-light-primary"} mb-1 me-2`}>
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-2 ms-0">
                      <IconFilter />
                    </span>
                    {lang.labels.filter}
                  </div>
                </Dropdown.Toggle>
                <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown w-300px w-md-325px" align="end">
                  <div className="mt-5 mx-5">
                    <div className="fs-5 text-dark fw-bolder">{lang.labels.filterOptions}</div>
                  </div>
                  <div className="separator border-gray-200"></div>
                  <div className="mt-3 m-5">
                    <div className="row mb-2">
                      <label className="col-5 col-form-label-sm fw-bold fs-7">{lang.labels.timeInterval}:</label>
                      <div className="col-7 align-content-center">
                        <Form.Select bsPrefix="form-select form-select-sm" aria-placeholder={lang.labels.select} name="interval" value={filter.interval} onChange={handleChangeInterval}>
                          <option value="" className="d-none">{lang.labels.select}</option>
                          <option value="today">{lang.labels.today}</option>
                          <option value="yesterday">{lang.labels.yesterday}</option>
                          <option value="week">{lang.labels.thisWeek}</option>
                          <option value="month">{lang.labels.thisMonth}</option>
                          <option value="year">{lang.labels.thisYear}</option>
                          { companyForUser?.created_at && companyYearList(companyForUser.created_at).filter((item) => (item !== new Date().getFullYear())).map (( (item, index) => { return (
                            <option key={index} value={item}>{lang.labels.year} {item}</option>
                          )}))}
                        </Form.Select>
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-md-6 mb-2 mb-md-0">
                        <label className="form-label fs-7">{lang.labels.startDate}:</label>
                        <input className="form-control form-control-sm" type="date" name="start-date" max={filter.end_date?.substring(0, 10) ?? ''} value={filter.start_date?.substring(0, 10) ?? ''} onChange={handleChangeStartDate} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fs-7">{lang.labels.endDate}:</label>
                        <input className="form-control form-control-sm" type="date" name="end-date" min={filter.start_date?.substring(0, 10) ?? ''} value={filter.end_date?.substring(0, 10) ?? ''} onChange={handleChangeEndDate} />
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="form-label fs-7">{lang.labels.customer}:</label>
                      <div ref={contactAutocompleteRef} style={{position: 'relative'}}>
                        <div className="d-flex align-items-center">
                          <input
                            className="form-control form-control-sm"
                            type="text"
                            placeholder={lang.labels.searchContact ?? 'Rechercher un contact...'}
                            value={contactSearch}
                            onChange={handleContactInputChange}
                            onFocus={() => { if (contactSearch.length >= 2) setContactDropdownOpen(true) }}
                          />
                          { filter.contact.length > 0 &&
                            <button type="button" className="btn btn-sm btn-icon btn-light-danger ms-1 p-0" style={{minWidth: '24px', height: '24px'}} onClick={handleClearContact}>
                              <span style={{fontSize: '12px', lineHeight: 1}}>&times;</span>
                            </button>
                          }
                        </div>
                        { contactDropdownOpen && contactResults.length > 0 &&
                          <div className="bg-white border rounded shadow-sm" style={{position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050, maxHeight: '200px', overflowY: 'auto'}}>
                            { contactResults.map((item: any, index: number) => (
                              <div key={index} className="px-3 py-2 cursor-pointer text-hover-primary" style={{cursor: 'pointer', fontSize: '13px'}} onClick={() => handleSelectContact(item.id)}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f8fa')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}>
                                {getContactDisplayName(item)}
                              </div>
                            ))}
                          </div>
                        }
                      </div>
                    </div>
                    { sale === "invoice" &&
                      <div className="mb-2">
                        <label className="form-label fs-7">{lang.labels.validity}:</label>
                        <Form.Select bsPrefix="form-select form-select-sm" aria-placeholder={lang.labels.select} name="validity" value={filter.validity} onChange={handleChangeValidity}>
                          <option value="" className="d-none">{lang.labels.select}</option>
                          { validityList(lang).map (( (item, index) => { return (
                            <option key={index} value={item.code}>{item.display}</option>
                          )}))}
                        </Form.Select>
                      </div>
                    }
                    <div className="mb-2">
                      <label className="form-label fs-7">{lang.labels.status}:</label>
                      <Form.Select bsPrefix="form-select form-select-sm" aria-placeholder={lang.labels.select} name="status" value={filter.status} onChange={handleChangeStatus}>
                        <option value="" className="d-none">{lang.labels.select}</option>
                        { status().map (( (item, index) => { return (
                          <option key={index} value={item.code}>{item.display}</option>
                        )}))}
                      </Form.Select>
                    </div>
                    <div className="mb-2">
                      <label className="form-label fs-7">{lang.labels.expiration}:</label>
                      <Form.Select bsPrefix="form-select form-select-sm" aria-placeholder={lang.labels.select} name="expiration" value={filter.expiration} onChange={handleChangeExpiration}>
                        <option value="" className="d-none">{lang.labels.select}</option>
                        { expirationList(lang).map (( (item, index) => { return (
                          <option key={index} value={item.code}>{item.display}</option>
                        )}))}
                      </Form.Select>
                    </div>
                    { sale === "invoice" &&
                      <div className="mb-2">
                        <label className="form-label fs-7">{lang.labels.payment}:</label>
                        <Form.Select bsPrefix="form-select form-select-sm" aria-placeholder={lang.labels.select} name="payment-status" value={filter.payment} onChange={handleChangePayment}>
                          <option value="" className="d-none">{lang.labels.select}</option>
                          { paymentStatusList(lang).map (( (item, index) => { return (
                            <option key={index} value={item.code}>{item.display}</option>
                          )}))}
                        </Form.Select>
                      </div>
                    }
                    <div className="d-flex justify-content-end mt-5">
                      <Dropdown.Item bsPrefix="btn btn-sm btn-light btn-active-light-primary" onClick={executeFilterReset}>{lang.labels.reset}</Dropdown.Item>
                      <Dropdown.Item bsPrefix="btn btn-sm btn-primary ms-3" onClick={executeFilterApply}>{lang.labels.apply}</Dropdown.Item>
                    </div>
                  </div>
                </Dropdown.Menu>
              </Dropdown>
              <Dropdown>
                <Dropdown.Toggle variant={`btn ${button.group ? "btn-primary" : "btn-light-primary"} mb-1 me-2`}>
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-2 ms-0">
                      <IconSalelistheader7 />
                    </span>
                    {lang.labels.groupBy}
                  </div>
                </Dropdown.Toggle>
                <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown w-150px w-lg-175px" align="end">
                  <Dropdown.Divider />
                  <Dropdown.Item bsPrefix="dropdown-item fw-bold m-0 py-1" active={filter.group === "customer"} onClick={() => executeGroupList("customer")}>{lang.labels.customer}</Dropdown.Item>
                  { sale === "invoice" &&
                    <Dropdown.Item bsPrefix="dropdown-item fw-bold m-0 py-1" active={filter.group === "validity"} onClick={() => executeGroupList("validity")}>{lang.labels.validity}</Dropdown.Item>
                  }
                  <Dropdown.Item bsPrefix="dropdown-item fw-bold m-0 py-1" active={filter.group === "status"} onClick={() => executeGroupList("status")}>{lang.labels.status}</Dropdown.Item>
                  <Dropdown.Item bsPrefix="dropdown-item fw-bold m-0 py-1" active={filter.group === "expiration"} onClick={() => executeGroupList("expiration")}>{lang.labels.expiration}</Dropdown.Item>
                  { sale === "invoice" &&
                    <Dropdown.Item bsPrefix="dropdown-item fw-bold m-0 py-1" active={filter.group === "payment"} onClick={() => executeGroupList("payment")}>{lang.labels.payment}</Dropdown.Item>
                  }
                  <Dropdown.Item bsPrefix="dropdown-item fw-bold m-0 py-1" active={filter.group === "year"} onClick={() => executeGroupList("year")}>{lang.labels.year}</Dropdown.Item>
                  <Dropdown.Item bsPrefix="dropdown-item fw-bold m-0 py-1" active={filter.group === "month"} onClick={() => executeGroupList("month")}>{lang.labels.month}</Dropdown.Item>
                  <Dropdown.Item bsPrefix="dropdown-item fw-bold m-0 py-1" active={filter.group === "week"} onClick={() => executeGroupList("week")}>{lang.labels.week}</Dropdown.Item>
                  <Dropdown.Item bsPrefix="dropdown-item fw-bold m-0 py-1" active={filter.group === "day"} onClick={() => executeGroupList("day")}>{lang.labels.day}</Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item bsPrefix="dropdown-item fw-bolder m-0 py-1" onClick={() => executeGroupList("")}>{lang.labels.doNotGroup}</Dropdown.Item>
                  <Dropdown.Divider />
                </Dropdown.Menu>
              </Dropdown>
              <button className="btn btn-primary mb-1 me-2" type="button" onClick={executeShowModalExport}>
                <div className="d-flex align-items-center">
                  <span className="svg-icon svg-icon-2 ms-0">
                    <IconDownload />
                  </span>
                  {lang.labels.export}
                </div>
              </button>
              <button className="btn btn-primary mb-1 me-0" type="button" onClick={executeNavigateCreate}>
                <div className="d-flex align-items-center">
                  <span className="svg-icon svg-icon-2 ms-0">
                    <IconCross />
                  </span>
                  {lang.labels.addDocument}
                </div>
              </button>
            </div>
          </div>
          { storage && button.filter &&
            <div className="card-scroll d-flex bg-light-primary rounded border-0 p-3 mx-7 mb-4">
              <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.interval.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("interval")}>
                <AppStorageRemove attribute={lang.labels.interval} description={storage.interval}></AppStorageRemove>
              </button>
              <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.start_date.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("start_date")}>
                <AppStorageRemove attribute={lang.labels.start} description={dateFormat(lang.code, storage.start_date)}></AppStorageRemove>
              </button>
              <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.end_date.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("end_date")}>
                <AppStorageRemove attribute={lang.labels.end} description={dateFormat(lang.code, storage.end_date)}></AppStorageRemove>
              </button>
              <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.contact.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("contact")}>
                <AppStorageRemove attribute={lang.labels.contact} description={storage.contact_label || contactSelected(contactsForCompany ?? [], storage.contact)}></AppStorageRemove>
              </button>
              { storage.validity &&
                <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.validity.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("validity")}>
                  <AppStorageRemove attribute={lang.labels.validity} description={validityValue(lang, storage.validity)}></AppStorageRemove>
                </button>
              }
              <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.status.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("status")}>
                <AppStorageRemove attribute={lang.labels.status} description={statusDocumentValue(lang, storage.status)}></AppStorageRemove>
              </button>
              <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.expiration.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("expiration")}>
                <AppStorageRemove attribute={lang.labels.expiration} description={expirationValue(lang, storage.expiration)}></AppStorageRemove>
              </button>
              { storage.payment &&
                <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.payment.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("payment")}>
                  <AppStorageRemove attribute={lang.labels.payment} description={paymentStatusValue(lang, storage.payment)}></AppStorageRemove>
                </button>
              }
            </div>
          }
        </>
      }
      <AppModalQuoteExport companyForUser={companyForUser} grouped={{state: button.group, desc: filter.group}} quotes={records as Quote[]} quoteGroups={recordGroups as QuoteGroup[]} filter={filter} vatSubject={vatSubject}></AppModalQuoteExport>
      <AppModalOrderExport companyForUser={companyForUser} grouped={{state: button.group, desc: filter.group}} orders={records as Order[]} orderGroups={recordGroups as OrderGroup[]} filter={filter} vatSubject={vatSubject}></AppModalOrderExport>
      <AppModalInvoiceExport companyForUser={companyForUser} grouped={{state: button.group, desc: filter.group}} invoices={records as Invoice[]} invoiceGroups={recordGroups as InvoiceGroup[]} filter={filter} vatSubject={vatSubject}></AppModalInvoiceExport>
    </>
  )
};

export default AppSaleListHeader;
